/**
 * Billing fields that arrive with a sensible value the client can overwrite —
 * legacy prefilled them with mount-time effects, which §11.1 forbids here.
 *
 * The pattern is the payment method's: the default is resolved at RENDER
 * for display and threaded into `serializePlacement` so the wire carries what
 * the client actually saw. Nothing is written to the persisted slice until the
 * client edits the field, so a draft is never dirtied by simply visiting the
 * step.
 */

import type { PropertyRef } from 'models/purchaseFlowRevamp'

/** Legacy's cap on the invoice reference (`maxReferenceCharacters`). */
export const MAX_REFERENCE_CHARACTERS = 250

/** Legacy stripped newlines from the reference — it is a single-line field. */
export const sanitizeReference = (value: string): string =>
  value.replace(/(\r\n|\n|\r)/gm, '').slice(0, MAX_REFERENCE_CHARACTERS)

/**
 * The invoice reference a fresh order starts with: the property's title, else
 * the client integration's own reference id, else nothing (legacy's order,
 * `PurchaseFlowOrderMeta.context.ts:312-318`).
 *
 * Deliberately NOT the address: the reference is what the client recognises the
 * order by on an invoice, and legacy never used the address for it.
 *
 * @example
 * resolveDefaultReference({ title: 'Villa Rosa', … })            // 'Villa Rosa'
 * resolveDefaultReference({ objectReferenceId: 'OBJ-7', … })     // 'OBJ-7'
 */
export const resolveDefaultReference = (property: PropertyRef | null | undefined): string | undefined => {
  const source = property?.title || property?.objectReferenceId
  if (!source) return undefined
  return sanitizeReference(source) || undefined
}

/**
 * The CC list a fresh order starts with: the client's own saved CC emails plus
 * the ones saved on their workspace membership, deduped (legacy merged both
 * sets, `PurchaseFlowOrderMeta.context.ts:352-356`).
 */
export const resolveDefaultCcEmails = (
  userCcEmails: ReadonlyArray<string> | undefined,
  workspaceCcEmails: ReadonlyArray<string> | undefined
): string[] => [...new Set([...(userCcEmails ?? []), ...(workspaceCcEmails ?? [])])]

/**
 * Which CC list to show and send. The stored list wins once the client has
 * touched it — including when they emptied it, which is why the marker exists:
 * keying on "is the array empty" alone would resurrect the prefill the moment
 * they removed the last address.
 *
 * Caveat: the marker is session state, so a resumed draft whose list the client
 * deliberately emptied shows the prefill again. Legacy overwrote the list on
 * every mount, so this is still strictly better than the behaviour it replaces.
 */
export const resolveEffectiveCcEmails = (
  stored: ReadonlyArray<string>,
  touched: boolean | undefined,
  prefill: ReadonlyArray<string>
): string[] => (touched || stored.length > 0 ? [...stored] : [...prefill])
